<script lang="ts">
  import { goto } from "$app/navigation";
  import { page } from "$app/state";
  import { SearchHistory } from "$lib/components";
  import Tooltip from "$lib/components/basic/tooltip/tooltip.svelte";
  import ListHistory from "$lib/components/search-list/listHistory/listHistory.svelte";
  import { SearchHistoryDev } from "$lib/components/search/searchHistoryDev";
  import TableHistory from "$lib/components/table/tableHistory/tableHistory.svelte";
  import { Button } from "$lib/components/ui";
  import { routes } from "$lib/routes";
  import { authStore } from "$lib/stores";
  import { outboxMessagesStore } from "$lib/stores/outboxStore";
  import { sidePanelStore } from "$lib/stores/sidePanelStore";
  import { tableStore } from "$lib/stores/table-store";
  import CreateTableDialog from "$lib/table/components/CreateTableDialog.svelte";
  import ShortcutKey from "$lib/table/components/ShortcutKey.svelte";
  import { cn } from "$lib/utility";
  import { Plus } from "lucide-svelte";
  import { mode } from "mode-watcher";
  import { onDestroy, onMount } from "svelte";
  import type { TransitionConfig } from "svelte/transition";
  import { fade } from "svelte/transition";

  let {
    className = "",
    panelContent = "search-history",
    transition = fade,
    transitionParams = { duration: 300 },
  } = $props<{
    className?: string;
    panelContent?: string;
    transition?: (node: Element, params?: any) => TransitionConfig;
    transitionParams?: Record<string, any>;
  }>();

  let panelContainer = $state<HTMLElement | null>(null);
  let isContentScrolled = $state(false);

  const baseClasses = "flex flex-col z-0";
  const borderColor = $derived($mode === "dark" ? "border-theme-background-secondary" : "border-black");

  let wrapperClasses = $derived(
    cn(
      baseClasses,
      "w-[320px] border-black border-r dark:border-theme-background-primary h-screen z-1",
      "transform transition-all duration-200 ease-in-out",
      $sidePanelStore.isOpen ? "translate-x-0" : "-translate-x-full",
      className,
      $mode === "dark" ? "bg-theme-background-secondary text-theme-text-primary" : "bg-white text-black",
    ),
  );

  function handleContentScroll(event: Event) {
    event.stopPropagation();
    isContentScrolled = !!panelContainer && panelContainer.scrollTop > 0;
  }

  function preventScroll(event: Event) {
    event.stopPropagation();
  }

  onMount(() => {
    if (panelContainer) {
      panelContainer.addEventListener("scroll", handleContentScroll, { passive: true });
      panelContainer.addEventListener("touchmove", preventScroll, { passive: false });
    }
  });

  onDestroy(() => {
    if (panelContainer) {
      panelContainer.removeEventListener("scroll", handleContentScroll);
      panelContainer.removeEventListener("touchmove", preventScroll);
    }
  });
</script>

{#key page.url}
  {#if panelContent}
    <div class="relative">
      <CreateTableDialog />
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
      <aside class={wrapperClasses} use:transition={transitionParams} onclick={(e: Event) => e.stopPropagation()}>
        <div class="relative flex max-h-screen min-h-0 flex-col">
          <div
            class={cn(
              "flex w-full items-center justify-between border-theme-text-primary py-3 px-4",
              isContentScrolled ? `border-b dark:border-white/20` : "",
            )}
          >
            {#if panelContent.startsWith("search-history")}
              <span class="text-lg font-bold">Searches</span>
              <Tooltip side="bottom" content="New search [⌘ ⇧ O]">
                <Button
                  variant="ghost"
                  onclick={() => goto($authStore.isLoggedIn ? routes.searches : routes.root)}
                  class="flex h-5 w-5 items-center justify-center !rounded-base border-1 border-theme-text-primary p-0 focus:outline-none focus:ring-0"
                >
                  <Plus size={24} strokeWidth={1} class="hidden sm:inline-block" />
                </Button>
              </Tooltip>
            {:else if panelContent === "table-history"}
              <div class="flex items-center gap-2">
                <span class="text-lg font-bold">Chatsheet</span><span
                  class="shrink-0 rounded-full border border-green-300 bg-green-200 px-2 py-0.5 text-[9px] text-green-900 dark:border-green-500 dark:bg-green-900 dark:text-green-100"
                >
                  ALPHA
                </span>
              </div>
            {:else if panelContent === "approvals-list"}
              <span class="text-lg font-bold">Outbox</span>
            {:else if panelContent === "search-lists"}
              <span class="text-lg font-bold">Lists</span>
            {/if}
          </div>

          <div class="custom-scrollbar overflow-y-auto px-4" bind:this={panelContainer}>
            {#if panelContent === "search-history"}
              <SearchHistory />
            {:else if panelContent === "search-history-dev"}
              <SearchHistoryDev />
            {:else if panelContent === "table-history"}
              <TableHistory />
            {:else if panelContent === "search-lists"}
              <ListHistory />
            {:else if panelContent === "approvals-list"}
              {#each $outboxMessagesStore as message}
                <!-- Existing outbox message rendering -->
              {/each}
            {/if}
          </div>
        </div>
      </aside>
    </div>
  {/if}
{/key}

<style>
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background-color: transparent;
  }
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
</style>
